<script lang="ts">
    
    import { createEventDispatcher } from 'svelte';    
    import { fly, fade } from 'svelte/transition';
    import type { AnswerDto, QuestionDto } from "../../global";

    type AnswerState = {
        answer: AnswerDto;
        isWin: boolean;
        isFail: boolean;
    };
    
    export let question: QuestionDto | null = null;

    const dispatch = createEventDispatcher();
    
    let currentQuestion: QuestionDto | null;
    let showQuestion = false;
    let answerStates: AnswerState[];
    let canAnswer = false;

    $: updateQuestion(question);

    function updateQuestion(question: QuestionDto | null): void {
        canAnswer = false;
        if (!showQuestion) {
            setCurrentQuestion(question);
        } else {
            showQuestion = false;
        }
    }

    function setCurrentQuestion(question: QuestionDto | null): void {
        if (!question) {
            currentQuestion = null;
            showQuestion = false;
            canAnswer = false;
            return;
        }

        answerStates = question.answers.map(answer => ({
            answer,
            isFail: false,
            isWin: false,
        }));

        answerStates.sort(() => -0.5 + Math.random());

        currentQuestion = question;
        showQuestion = true;
        canAnswer = true;
    }

    function onAnswer(answerState: AnswerState): void {
        if (!canAnswer) {
            return;
        }

        canAnswer = false;

        if (answerState.answer.right) {
            answerState.isWin = true;
            dispatch('win', answerState.answer);
        } else {
            answerState.isFail = true;
            setTimeout(() => {
                const rightAnwerState = answerStates.find(({ answer }) => !!answer.right);
                if (rightAnwerState) {
                    rightAnwerState.isWin = true;
                }
                answerStates = answerStates;
            }, 600);
            dispatch('fail', answerState.answer);
        }
        answerStates = answerStates;
    }

    function questionHandler(questionWrapper: Element) {
        return {
            destroy() {
                setCurrentQuestion(question);
            }
        };
    }

</script>

{#if showQuestion}
    <div class="question" use:questionHandler>
        <div
            class="header"
            in:fly={{ duration: 200, x: 50 }}
            out:fly={{ duration: 200, x: -50 }}
            >
            {#if question.image}
                <img
                    class="image"
                    src={question.image}
                    alt=""
                    >
            {/if}
            {#if question.statement}    
                <div class="statement">
                    <div class="text" transition:fade>{question.statement}</div>
                </div>
            {/if}
        </div>
        <div class="answers">
            {#each answerStates as answerState, index}
                <div
                    class="answer shadow"
                    class:is-win={answerState.isWin}
                    class:is-fail={answerState.isFail}
                    on:click={() => onAnswer(answerState)}
                    in:fly={{ x: 300, delay: 50 * index }}
                    out:fly={{ x: -300, delay: 50 * index }}
                    >
                    <span class="text">{answerState.answer.text}</span>
                </div>
            {/each}
        </div>
    </div>
{/if}

<style lang="scss">

    @use '../../variables';

    .question {
        height: 100%;
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }

    .header {
        // flex: 1 1 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: calc(1vh + 1vw);
    }

    .image {
        display: block;
        width: 100%;
        aspect-ratio: 4 / 3;
        object-fit: contain;
    }

    .statement {
        color: #fff;
        font-family: var(--font-londrina);
        text-align: center;
        font-size: calc(1.2vh + 1.2vw);
        min-height: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;

        @media screen and (max-width: 768px) {
            font-size: calc(1.7vh + 1.7vw);
        }
    }

    .answer {
        position: relative;
        background-color: #fff;
        padding: 1rem;
        text-align: center;
        border-radius: 1000px;
        margin-top: calc(1vh + 1vw);
        color: #000;
        font-size: calc(1.2vh + 1.2vw);

        @media screen and (max-width: 768px) {
            font-size: calc(1.7vh + 1.7vw);
        }

        &.is-win, &.is-fail {
            color: #fff;
        }

        &.is-win {
            background-color: var(--win-color) !important;
            transition: background-color 0.3s ease-in-out;
        }

        &.is-fail {
            background-color: var(--fail-color) !important;
            transition: background-color 0.3s ease-in-out;

        }

        .text {
            position: relative;
            z-index: 2;
        }
    }

</style>

